import { ensureGsap, prefersReducedMotion } from "@/shared/lib/gsap";
import { useEffect, useRef } from "react";

interface RevealOptions {
  y?: number;
  delay?: number;
  duration?: number;
  stagger?: number;
  childrenSelector?: string;
  start?: string;
}

/** Fade + slide-up reveal triggered on scroll. Returns ref to attach to root element. */
export function useGsapReveal<T extends HTMLElement = HTMLDivElement>(options: RevealOptions = {}) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const gsap = ensureGsap();
    const el = ref.current;
    if (!gsap || !el) return;

    const childrenSelector = options.childrenSelector;

    if (prefersReducedMotion()) {
      gsap.set(el, { opacity: 1, y: 0 });
      if (childrenSelector) {
        gsap.set(el.querySelectorAll(childrenSelector), { opacity: 1, y: 0 });
      }
      return;
    }

    const targets = childrenSelector ? Array.from(el.querySelectorAll(childrenSelector)) : [el];

    gsap.set(targets, { opacity: 0, y: options.y ?? 40 });

    const tween = gsap.to(targets, {
      opacity: 1,
      y: 0,
      duration: options.duration ?? 0.8,
      ...(options.delay !== undefined ? { delay: options.delay } : {}),
      stagger: options.stagger ?? 0,
      scrollTrigger: {
        trigger: el,
        start: options.start ?? "top 80%",
        once: true,
      },
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [
    options.y,
    options.delay,
    options.duration,
    options.stagger,
    options.childrenSelector,
    options.start,
  ]);

  return ref;
}
